/**
 * TaskTypeManagerDialog — Jun 2026
 * Admin editor for ``create_task`` task types: label, routing target
 * (who the spawned task goes to — e.g. medical records manager role),
 * whether a due date is required, and default category. New types can
 * be added without a code change; the built-in Reschedule type can be
 * edited but not deleted.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Pencil, Trash2, Plus, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TARGET_LABELS = {
  provider_scheduler: "Provider's scheduler",
  provider_biller: "Provider's assigned biller",
  scheduler: 'First scheduler (any)',
  biller: 'First biller',
  billing_manager: 'First billing manager',
  supervisor: "Provider's supervisor",
  current_user: 'The provider themselves',
  specific_user: 'Specific user…',
  role: 'Users with a role…',
};

const EMPTY = {
  label: '',
  target_assignee_type: 'role',
  target_role_name: '',
  specific_user_id: '',
  due_date_required: true,
  open_note_on_click: false,
  task_category: 'administrative',
};

export const TaskTypeManagerDialog = ({ open, onOpenChange, users = [], onSaved }) => {
  const [types, setTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | {key?} & form
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/form-tasks/task-types`);
      setTypes(data.task_types || []);
    } catch {
      toast.error('Failed to load task types');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    load();
    axios.get(`${API}/roles`).then((r) => setRoles((r.data || []).filter((x) => (x.name || '').toLowerCase() !== 'client'))).catch(() => {});
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...editing };
      if (editing.key) {
        await axios.put(`${API}/form-tasks/task-types/${editing.key}`, body);
      } else {
        await axios.post(`${API}/form-tasks/task-types`, body);
      }
      toast.success('Task type saved');
      setEditing(null);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save task type');
    } finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete task type "${t.label}"? Existing tasks are unaffected.`)) return;
    try {
      await axios.delete(`${API}/form-tasks/task-types/${t.key}`);
      toast.success('Task type deleted');
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete task type');
    }
  };

  const targetSummary = (t) => {
    if (t.target_assignee_type === 'role') return `Role: ${t.target_role_name || '—'}`;
    if (t.target_assignee_type === 'specific_user') {
      const u = users.find((x) => x.id === t.specific_user_id);
      return `User: ${u?.full_name || t.specific_user_id || '—'}`;
    }
    return TARGET_LABELS[t.target_assignee_type] || t.target_assignee_type;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="task-type-manager-dialog">
        <DialogHeader>
          <DialogTitle>Task Types</DialogTitle>
          <DialogDescription>
            Each type routes its tasks to a target (role or person) and controls whether a due date is required.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-gray-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</div>
        ) : editing ? (
          <div className="space-y-3 border rounded-lg p-4 bg-slate-50" data-testid="task-type-edit-form">
            <div>
              <Label className="text-xs">Name</Label>
              <Input
                value={editing.label}
                maxLength={60}
                placeholder="e.g. Fax to PCP"
                onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                data-testid="task-type-label-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Route tasks to</Label>
                <select
                  value={editing.target_assignee_type}
                  onChange={(e) => setEditing({ ...editing, target_assignee_type: e.target.value })}
                  className="block w-full h-9 rounded border border-slate-300 bg-white px-2 text-sm"
                  data-testid="task-type-target-select"
                >
                  {Object.entries(TARGET_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
              <div>
                {editing.target_assignee_type === 'role' && (
                  <>
                    <Label className="text-xs">Target role</Label>
                    <select
                      value={editing.target_role_name || ''}
                      onChange={(e) => setEditing({ ...editing, target_role_name: e.target.value })}
                      className="block w-full h-9 rounded border border-slate-300 bg-white px-2 text-sm"
                      data-testid="task-type-role-select"
                    >
                      <option value="">— choose a role —</option>
                      {roles.map((r) => (
                        <option key={r.id} value={r.name}>{r.name}</option>
                      ))}
                    </select>
                  </>
                )}
                {editing.target_assignee_type === 'specific_user' && (
                  <>
                    <Label className="text-xs">Target user</Label>
                    <select
                      value={editing.specific_user_id || ''}
                      onChange={(e) => setEditing({ ...editing, specific_user_id: e.target.value })}
                      className="block w-full h-9 rounded border border-slate-300 bg-white px-2 text-sm"
                      data-testid="task-type-user-select"
                    >
                      <option value="">— choose a user —</option>
                      {users.filter((u) => !u.is_archived).map((u) => (
                        <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Default category</Label>
                <select
                  value={editing.task_category || 'administrative'}
                  onChange={(e) => setEditing({ ...editing, task_category: e.target.value })}
                  className="block w-full h-9 rounded border border-slate-300 bg-white px-2 text-sm"
                  data-testid="task-type-category-select"
                >
                  <option value="scheduling">Scheduling</option>
                  <option value="billing">Billing</option>
                  <option value="clinical">Clinical</option>
                  <option value="administrative">Administrative</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm mt-5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editing.due_date_required !== false}
                  onChange={(e) => setEditing({ ...editing, due_date_required: e.target.checked })}
                  data-testid="task-type-due-required-checkbox"
                />
                Due date required
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editing.open_note_on_click === true}
                  onChange={(e) => setEditing({ ...editing, open_note_on_click: e.target.checked })}
                  data-testid="task-type-open-note-checkbox"
                />
                Opening the task opens the source document (note) — for download / fax workflows
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(null)} disabled={saving} data-testid="task-type-cancel-btn">
                Cancel
              </Button>
              <Button size="sm" onClick={save} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="task-type-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Save Task Type
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setEditing({ ...EMPTY })} className="bg-purple-600 hover:bg-purple-700" data-testid="task-type-add-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Task Type
              </Button>
            </div>
            {types.map((t) => (
              <div key={t.key} className="flex items-center justify-between border rounded-lg px-3 py-2 bg-white" data-testid={`task-type-row-${t.key}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{t.label}</span>
                    {t.is_system && <Badge variant="outline" className="text-[10px]">Built-in</Badge>}
                  </div>
                  <div className="text-xs text-gray-500">
                    → {targetSummary(t)} · Due date {t.due_date_required !== false ? 'required' : 'optional'} · {t.task_category || 'administrative'}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setEditing({ ...EMPTY, ...t })} data-testid={`task-type-edit-${t.key}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {!t.is_system && (
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => remove(t)} data-testid={`task-type-delete-${t.key}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TaskTypeManagerDialog;
